import { action, computed, observable } from 'mobx';
import {
    get,
    filter
} from 'lodash/fp';
// data imports
import { TileTypes, metricProperties } from 'logic/data/Izenda.ReportPartKPIConstants';

// local imports
import TextTileProperties from './Izenda.TextTileProperties';
import MetricTileProperties from './Izenda.MetricTileProperties';
import ImageTileProperties from './Izenda.ImageTileProperties';

// share imports
import tracker from 'share/tracking/Izenda.Tracker';

/**
 * @class ReportPartKPITile
 * @description describes a position, size, and contents of a KPI tile
 */
@tracker
export default class ReportPartKPITile {
    @observable id = '';
    @observable left = 0;
    @observable top = 0;
    @observable width = 0;
    @observable height = 0;
    @observable isFullSize = true;
    @observable isLoading = false;
    @observable autoSize = false;
    @observable tileType = '';
    @observable value = '';
    @observable properties = null;

    constructor(tileData) {
        this.initializeTile(tileData);
    }

    /**
     * @method initializeTile
     * @description initialize all properties of class
     * @param {object} tileData data that describes the initial position and type of tile that was added to the kpi grid
     */
    @action
    initializeTile(tileData) {
        this.id = tileData.id || this.id;
        this.tileType = tileData.tileType || this.tileType;
        this.left = tileData.left || this.left;
        this.top = tileData.top || this.top;
        this.width = tileData.width && !tileData.isNew ? tileData.width : 4;
        this.value = tileData.value || this.value;
        if (!tileData.height || tileData.isNew) {
            this.height = tileData.tileType === TileTypes.text.id ? 1 : 4;
        } else {
            this.height = tileData.height;
        }

        this.setProperties(tileData);
    }

    /**
     * @method updateValue
     * @description update the contents of a tile
     * @param {string} value contents of a tile (text or image source)
     */
    @action
    updateValue(value) {
        this.value = value;
        this.markDirty();
    }

    /**
     * @method updatePosition
     * @description update the position of the tile
     * @param {object} position new x and y coordinates for a tile within kpi grid
     */
    @action
    updatePosition(position) {
        this.height = position.height || this.height;
        this.width = position.width || this.width;
        this.left = position.left || this.left;
        this.top = position.top || this.top;
    }

    /**
     * @method setProperties
     * @description set properties for the tile based on the type of tile
     * @param {string} type tile type
     */
    @action
    setProperties(tileData) {
        switch (tileData.tileType) {
            case TileTypes.text.id:
                this.properties = new TextTileProperties(tileData.properties);
                break;
            case TileTypes.metric.id:
                this.properties = new MetricTileProperties(tileData.properties);
                break;
            case TileTypes.image.id:
                this.properties = new ImageTileProperties(tileData.properties);
                break;
            default:
                // TODO: remove placeholder object and replace with property classes for image and metric tiles
                this.properties = {};
                break;
        }
    }

    /**
     * @property {boolean} isImageTile
     * @description indicates if the tile is an image tile
     */
    @computed
    get isImageTile() {
        return this.tileType === TileTypes.image.id;
    }

    /**
     * @property {boolean} isImageOrMetricTile
     * @description indicates if the tile is an image or a metric tile
     */
    @computed
    get isImageOrMetricTile() {
        return this.isImageTile || this.tileType === TileTypes.metric.id;
    }

    /**
     * @method getPropertiesByKey
     * @description get a tile property by the name of it
     * @param {string} key name of property that is needed
     */
    getPropertiesByKey(key) {
        return this.properties[key];
    }


    /**
     * Check uniq link option in : Custom URL , Embbeded Javascript, Sub Report
     * @param {bool} popupContent
     * @param {string} currentPopupName
     * @returns {bool} this link option is uniq or not
     */
    checkUniqLinkOption = function (popupContent, currentPopupName) {
        var excludeCurrentPopup = filter(function (option) {
            return option != currentPopupName;
        }, metricProperties.CHECK_UNIQ_OPTIONS);

        var isValid = true;

        excludeCurrentPopup.forEach(
            function (option) {
                var selectedIconValue = this[option].selectedIconValue || {};

                // If option contains style = Embedded, skip this option
                var isEmbeddedSubReport = false;


                var isEmptySubReport =
                    option === metricProperties.SUB_REPORT.NAME ? !get('selectedReport', this[option]) : false;

                if (
                    !isEmbeddedSubReport &&
                    !isEmptySubReport &&
                    !this[option].viewSettingByLink && // only check for icon option
                    popupContent.viewSettingByLink &&
                    selectedIconValue.value === popupContent.selectedIconValue.value
                ) {
                    isValid = false;
                }
            }.bind(this)
        );

        return isValid;
    };
}



// WEBPACK FOOTER //
// ./ui/logic/models/ReportPartKPIModels/Izenda.ReportPartKPITile.js